import './ListModeControl.scss';
import React, { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DataTable, { IDataTableColumn, IDataTableHandle } from '@srm/ui/data-table';
import { IDataTableSource, IServerLoadOptions } from '@srm/ui/data-table/server';
import { DictionariesService } from '@services/DictionariesService';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IDictFilter } from '@models/Forms/IForms';
import { IDictSettings } from '@atoms/Dictpicker/IDictSettings';
import {
    dictBooleanFilter,
    dictPresetFilter,
    filterGridRowsByScript,
    filterResponseVoid,
    mergeDictFilter,
} from '@utils/dictionaryFilters';
import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';
import { IDictpickerRefActions } from '@atoms/Dictpicker/IDictpickerRefActions';
import { ConfigService } from '@/configuration/services/configService';
import { validateLoadedData } from '@utils/helpersDictPicker';
import { useUserScripts } from '@hooks/useUserScripts';
import {
    parseDictionarySelectableLevels,
    isDictionaryLevelSelectable,
    withDictionarySelectionLevels,
    DictionarySelectionRow,
} from '../../dictionarySelectableLevels';
import { buildDictColumns, buildDictSort } from './dictGridColumns';

export interface IListModeControlProp extends IDictSettings {
    onSelectedRowItems: (value: IDictionaryData[]) => void;
    onConfirmRow?: (row: IDictionaryData) => void;
    onSelectedKeys: (value: string[]) => void;
    dictAttributes: string[];
    filterResponse: IDictFilter;
    controlRef: React.Ref<IDictpickerRefActions>;
}

/** Ширина списка: сумма ширин колонок шаблона плюс место под чекбокс и отступы. */
const EXTRA_WIDTH = 100;
/** Клиентская обработка: выборка приходит одним запросом целиком (`take=0` — без LIMIT), дальше фильтрует таблица. */
const CLIENT_MODE_TAKE = 0;

const NO_ROWS: IDictionaryData[] = [];

/**
 * Счётчик строк для таблицы после клиентского дофильтра порции. Порции адресуются позициями серверной выборки,
 * поэтому таблице уходит её размер: уменьшенный дофильтром, он остановил бы догрузку прокруткой раньше конца.
 * Точное число показанных строк — только когда первая порция вместила всю выборку и следующей и так нет.
 */
const tableTotalCount = (options: IServerLoadOptions, serverRows: number, serverTotal: number, shown: number) =>
    options.skip === 0 && serverRows >= serverTotal && shown <= options.take ? shown : serverTotal;

/** Порция закрывает серверную выборку: после неё сервер строк не отдаст. */
const endsSelection = (options: IServerLoadOptions, serverRows: number, serverTotal: number) =>
    !options.take || serverRows < options.take || options.skip + serverRows >= serverTotal;

/**
 * Список справочника на базовом гриде.
 *
 * Строки берёт серверный источник таблицы: отбор, сортировка и страница уходят
 * на бэкенд проводным форматом `loadOptions`. Клиентская
 * обработка (`useClientSideDataProcessing`) грузит выборку целиком, и дальше её
 * фильтрует сама таблица.
 */
const ListModeControl: FC<IListModeControlProp> = (p: IListModeControlProp) => {
    const selectableRule = useMemo(() => parseDictionarySelectableLevels(p.selectableLevels), [p.selectableLevels]);
    const config = ConfigService.get();
    const applyFilterOnClick = config.application.applyGridFilterOnClick ?? false;
    const tableRef = useRef<IDataTableHandle<IDictionaryData>>(null);
    // Настройки пикера приходят новым объектом на каждый рендер модалки (spread
    // пропсов), а загрузчик и обработчики таблицы обязаны быть стабильными: новый
    // загрузчик — другая выборка (с начала, выбор снят). Читаем настройки через ref.
    const settingsRef = useRef(p);
    settingsRef.current = p;
    /** Скрытые из списка выбранные строки: они всё равно уходят в результат. */
    const hiddenRows = useRef<IDictionaryData[]>([]);
    /** Скрытый стартовый элемент можно убрать из нижнего лотка, хотя строки нет в таблице. */
    const removedHiddenKeysRef = useRef(new Set<string>());
    /** Выбор хранит ключи всех страниц, а таблица отдаёт строки только уже загруженных.
     *  Кэш нужен лотку и сабмиту, чтобы оба набора оставались полными. */
    const itemsCacheRef = useRef(new Map<string, IDictionaryData>());
    const selectionRunRef = useRef(0);
    /** Стартовый выбор ставится один раз — после первой порции строк. */
    const initialSelectionRef = useRef(false);
    const [reloadToken, setReloadToken] = useState(0);
    const clientMode = p.useClientSideDataProcessing ?? false;
    const [clientRows, setClientRows] = useState<IDictionaryData[]>();
    const [clientError, setClientError] = useState<unknown>();
    /** Размер серверной выборки из последнего ответа: «Выбрать все» просит её целиком, даже когда таблица знает
     *  только число строк после клиентского дофильтра. */
    const serverTotalRef = useRef(0);

    useImperativeHandle(p.controlRef, () => ({
        reset: () => {
            setReloadToken((token) => token + 1);
        },
        setSelected: (items: string[]) => {
            tableRef.current?.selectRows(items);
        },
        unSelect: (item: IDictionaryData) => {
            removedHiddenKeysRef.current.add(item.code);
            const { keys } = tableRef.current?.getSelected() ?? { keys: [] };
            tableRef.current?.selectRows(keys.filter((key) => key !== item.code));
        },
        clearSelection: () => {
            settingsRef.current.selected?.forEach((key) => removedHiddenKeysRef.current.add(key));
            tableRef.current?.clearSelection();
        },
    }));

    const preset = useMemo(
        () => (p.isFormData ? null : dictPresetFilter(p.filterResponse)),
        [p.isFormData, p.filterResponse],
    );

    /** Отбор псевдосправочника: строки фильтруются на клиенте — сервер их не умеет. */
    const applyFormDataFilters = useCallback((rows: IDictionaryData[]) => {
        const { filterResponse } = settingsRef.current;
        if (!filterResponse) return rows;
        let result = filterResponseVoid(rows, filterResponse);
        if (filterResponse.script) result = filterGridRowsByScript(result, filterResponse.script);
        return result;
    }, []);

    /** Внешние строки (`ExternalDataSource`) замещают одноимённые и идут в конец — в порцию, которой кончается
     *  выборка: добавленные к каждой порции, они повторялись бы при догрузке. */
    const applyExternalRows = useCallback((rows: IDictionaryData[], isLast: boolean) => {
        const external = settingsRef.current.getExternalDataSource?.();
        if (!external || external.length === 0) return rows;
        const kept = rows.filter((row) => external.findIndex((x) => x.code === row.code) === -1);
        return isLast ? [...kept, ...external] : kept;
    }, []);

    /** Уже выбранные строки прячутся из списка, но остаются в результате выбора. */
    const applyHideSelected = useCallback((rows: IDictionaryData[]) => {
        const { hideSelected, selected } = settingsRef.current;
        if (!hideSelected || !selected) return rows;
        const shown: IDictionaryData[] = [];
        const hidden: IDictionaryData[] = [];
        rows.forEach((row) => (selected.includes(row.code) ? hidden.push(row) : shown.push(row)));
        hiddenRows.current = [...hidden, ...hiddenRows.current];
        return shown;
    }, []);

    const fetchRows = useCallback(
        async (options: IServerLoadOptions) => {
            const s = settingsRef.current;
            // логические атрибуты справочник хранит числом — см. dictBooleanFilter
            const filter = dictBooleanFilter(mergeDictFilter(preset, options.filter));
            let params = `?${getLoadOptionsQuery({ ...options, filter })}`;
            params += '&formValues=' + encodeURIComponent(await s.getFormValuesAsync());
            if (s.predicatesCache) {
                params += '&predicates=' + s.predicatesCache;
            }

            if (s.isFormData) {
                const response = await DictionariesService.getGridFormdataItems(s.docId!, s.dictName, params);
                validateLoadedData(response.data.data, s.loadedDataValidators);
                const serverRows = response.data.data ?? [];
                const serverTotal = response.data.totalCount ?? serverRows.length;
                serverTotalRef.current = serverTotal;
                let rows = serverRows;
                if (s.filterResponse?.script || s.filterResponse?.filter) rows = applyFormDataFilters(rows);
                rows = applyExternalRows(rows, endsSelection(options, serverRows.length, serverTotal));
                rows = await withDictionarySelectionLevels(
                    rows,
                    parseDictionarySelectableLevels(s.selectableLevels),
                    s.isFormData,
                    (codes) => DictionariesService.getTreeItemsByCode(s.dictName, codes),
                );
                rows = applyHideSelected(rows);
                return { rows, totalCount: tableTotalCount(options, serverRows.length, serverTotal, rows.length) };
            }

            if (s.joinedDictionaries && s.joinedDictionaries.length > 0) {
                params += `&joinedDictionaries=${JSON.stringify(s.joinedDictionaries)}`;
            }
            const response = await DictionariesService.getGridItems(s.dictName, params);
            validateLoadedData(response.data.data, s.loadedDataValidators);
            const serverRows = response.data.data ?? [];
            const serverTotal = response.data.totalCount ?? serverRows.length;
            serverTotalRef.current = serverTotal;
            let rows = serverRows;
            // отбор по кодам делает сервер, а скрипт-фильтр — только клиент
            if (s.filterResponse?.script) rows = filterGridRowsByScript(rows, s.filterResponse.script);
            rows = await withDictionarySelectionLevels(
                rows,
                parseDictionarySelectableLevels(s.selectableLevels),
                s.isFormData,
                (codes) => DictionariesService.getTreeItemsByCode(s.dictName, codes),
            );
            return { rows, totalCount: tableTotalCount(options, serverRows.length, serverTotal, rows.length) };
        },
        [preset, applyFormDataFilters, applyExternalRows, applyHideSelected],
    );

    /** Стартовый выбор: список уже знает выбранные коды — отмечаем их после загрузки. */
    const markInitialSelection = useCallback(() => {
        const { hideSelected, selected } = settingsRef.current;
        if (initialSelectionRef.current || hideSelected) return;
        if (!selected || selected.length === 0) return;
        initialSelectionRef.current = true;
        tableRef.current?.selectRows(selected);
    }, []);

    const serverLoad = useCallback(
        async (options: IServerLoadOptions) => {
            const result = await fetchRows(options);
            markInitialSelection();
            return result;
        },
        [fetchRows, markInitialSelection],
    );

    // Клиентская обработка: выборка приходит целиком, дальше фильтры и страницы — в таблице. Отказ показывает
    // таблица с «Повторить», а не пустой список.
    useEffect(() => {
        if (!clientMode) return;
        let cancelled = false;
        setClientError(undefined);
        fetchRows({ skip: 0, take: CLIENT_MODE_TAKE, requireTotalCount: true }).then(
            (result) => {
                if (cancelled) return;
                setClientRows(result.rows);
                markInitialSelection();
            },
            (error: unknown) => {
                if (!cancelled) setClientError(error);
            },
        );
        return () => {
            cancelled = true;
        };
    }, [clientMode, fetchRows, markInitialSelection, reloadToken]);

    const retryClientLoad = useCallback(() => setReloadToken((token) => token + 1), []);

    // Смена справочника — это другой список: стартовый выбор ставим заново
    useEffect(() => {
        initialSelectionRef.current = false;
        hiddenRows.current = [];
        removedHiddenKeysRef.current.clear();
        itemsCacheRef.current.clear();
        selectionRunRef.current += 1;
    }, [p.dictName]);

    const columns = useMemo<IDataTableColumn<IDictionaryData>[]>(
        () =>
            buildDictColumns({
                dictAttributes: p.dictAttributes,
                gridAttribute: p.gridAttribute,
                isFormData: p.isFormData,
            }),
        [p.dictAttributes, p.gridAttribute, p.isFormData],
    );

    const defaultSort = useMemo(
        () => buildDictSort({ dictAttributes: p.dictAttributes, gridAttribute: p.gridAttribute }),
        [p.dictAttributes, p.gridAttribute],
    );

    /** Уровни справочника, доступные для выбора (`selectableLevels`). */
    const isRowSelectable = useCallback(
        (row: DictionarySelectionRow) =>
            isDictionaryLevelSelectable(selectableRule, row.selectionLevel, row.parent === '', !row.hasChild),
        [selectableRule],
    );

    const onSelectionChange = useCallback(
        (keys: string[], rows: IDictionaryData[]) => {
            const s = settingsRef.current;
            const run = ++selectionRunRef.current;
            [...rows, ...hiddenRows.current].forEach((row) => itemsCacheRef.current.set(row.code, row));
            const hiddenKeys =
                s.hideSelected && s.selected ? s.selected.filter((key) => !removedHiddenKeysRef.current.has(key)) : [];
            const reportedKeys = [...new Set([...keys, ...hiddenKeys])];
            const reportCached = () => {
                const restrictSelection = selectableRule.mode === 'range' || selectableRule.mode === 'invalid';
                // Не загруженный ключ остаётся ожидающим до проверки исходного уровня.
                const allowedKeys = restrictSelection
                    ? reportedKeys.filter((key) => {
                          const item = itemsCacheRef.current.get(key);
                          return !item || isRowSelectable(item);
                      })
                    : reportedKeys;
                const items = allowedKeys
                    .map((key) => itemsCacheRef.current.get(key))
                    .filter((item): item is IDictionaryData => !!item);
                s.onSelectedKeys(allowedKeys);
                s.onSelectedRowItems(items);
            };

            reportCached();
            const missing = reportedKeys.filter((key) => !itemsCacheRef.current.has(key));
            if (missing.length === 0 || s.isFormData) return;

            DictionariesService.getItemsByCode(s.dictName, missing)
                .then(async (response) => {
                    const loaded = await withDictionarySelectionLevels(
                        response.data ?? [],
                        selectableRule,
                        s.isFormData,
                        (codes) => DictionariesService.getTreeItemsByCode(s.dictName, codes),
                    );
                    if (run !== selectionRunRef.current) return;
                    loaded.forEach((item) => itemsCacheRef.current.set(item.code, item));
                    // Пока шёл запрос, пользователь мог изменить выбор: новый callback
                    // имеет свой run и сам доложит актуальный набор.
                    reportCached();
                })
                .catch(console.error);
        },
        [isRowSelectable, selectableRule],
    );

    /** Ключи всей выборки для «выбрать все»: тот же запрос без страницы. */
    const selectAllKeys = useCallback(
        async (options: IServerLoadOptions) => {
            const result = await fetchRows({ ...options, take: Math.max(options.take, serverTotalRef.current) });
            result.rows.forEach((row) => itemsCacheRef.current.set(row.code, row));
            return result.rows.filter(isRowSelectable).map((row) => row.code);
        },
        [fetchRows, isRowSelectable],
    );

    // Клиентская обработка грузит выборку сама (строки — в data), серверная — источником таблицы.
    const source = useMemo<IDataTableSource<IDictionaryData> | undefined>(
        () =>
            clientMode
                ? undefined
                : { load: serverLoad, refreshToken: reloadToken, keys: p.isMultiple ? selectAllKeys : undefined },
        [clientMode, serverLoad, reloadToken, p.isMultiple, selectAllKeys],
    );

    const totalWidth = useMemo(
        () => EXTRA_WIDTH + (p.gridAttribute?.attrs.reduce((sum, attr) => sum + attr.width, 0) || 0),
        [p.gridAttribute?.attrs],
    );

    // Пользовательские скрипты строки и ячейки: контекст повторяет прежнее
    // событие грида — его формат ждут скрипты конфигураций,
    // чтобы конфигурационные скрипты работали без правок
    const { onRowPreparedScript, onCellPreparedScript } = useUserScripts(
        p.userScripts?.onRowPrepared,
        p.userScripts?.onCellPrepared,
    );

    const onRowRendered = useCallback(
        ({ element, row, rowIndex }: { element: HTMLElement; row: IDictionaryData; rowIndex: number }) => {
            try {
                onRowPreparedScript?.({ rowElement: element, data: row, key: row.code, rowIndex, rowType: 'data' });
            } catch (error) {
                console.error('Скрипт строки справочника завершился ошибкой', error);
            }
        },
        [onRowPreparedScript],
    );

    const headerByField = useMemo(() => new Map(columns.map((column) => [column.field, column.header])), [columns]);

    const onCellRendered = useCallback(
        ({
            element,
            row,
            field,
            value,
            text,
        }: {
            element: HTMLElement;
            row: IDictionaryData;
            field: string;
            value: unknown;
            text: string;
        }) => {
            // значение ячейки в data-value: на него завязаны стили конфигураций
            if (!element.getAttribute('data-value')) element.setAttribute('data-value', text);
            try {
                onCellPreparedScript?.({
                    cellElement: element,
                    data: row,
                    key: row.code,
                    rowType: 'data',
                    column: { dataField: field, name: field, caption: headerByField.get(field) ?? field },
                    value,
                    text,
                    displayValue: text,
                });
            } catch (error) {
                console.error('Скрипт ячейки справочника завершился ошибкой', error);
            }
        },
        [onCellPreparedScript, headerByField],
    );

    return (
        <div className="dict-list" style={{ width: totalWidth }}>
            <DataTable<IDictionaryData>
                controlRef={tableRef}
                columns={columns}
                data={clientMode ? (clientRows ?? NO_ROWS) : NO_ROWS}
                getRowId={(row) => row.code}
                loading={clientMode && !clientRows && !clientError}
                error={clientMode ? clientError : undefined}
                onRetry={clientMode ? retryClientLoad : undefined}
                source={source}
                // список справочника идёт сплошным: строки догружаются прокруткой,
                // а под таблицей висит счётчик «Загружено X из Y»
                serverScrollMode="infinite"
                defaultSort={defaultSort}
                paging={false}
                selectable
                singleSelection={!p.isMultiple}
                selectOnRowClick
                isRowSelectable={isRowSelectable}
                selectAllMode={p.isMultiple ? 'allPages' : 'page'}
                onSelectionChange={onSelectionChange}
                onRowDoubleClick={p.isMultiple ? undefined : p.onConfirmRow}
                showFilterRow
                applyFiltersOnClick={applyFilterOnClick}
                defaultWrapMode={p.wordWrapEnabled ? 'wrap' : 'nowrap'}
                onRowRendered={onRowPreparedScript ? onRowRendered : undefined}
                onCellRendered={onCellRendered}
                fillHeight
                fitColumns
                overflowTooltip
                cacheRows
                emptyText="Нет строк"
            />
        </div>
    );
};

export default ListModeControl;
